/**
 * W3C `traceparent` generator for the browser. Phase 1 still skips the full
 * OpenTelemetry browser SDK — we only need outbound API requests to carry a
 * valid `traceparent` so backend OTel spans continue the chain from the
 * browser interaction. Real browser spans + export are phase 2.
 *
 * Trace lifetime: per outbound request. Every API call originates a fresh
 * traceId so each request shows up as its own root trace in Tempo. The
 * gateway and downstream services form the rest of that trace. This is a
 * deliberate trade — we lose correlation across multiple calls in one user
 * action, but the implementation stays trivial and the backend traces stay
 * readable instead of becoming page-lifetime mega-traces.
 */

const HEX = '0123456789abcdef';

function randomHex(byteLength: number): string {
  const cryptoApi: Crypto | undefined =
    typeof crypto !== 'undefined' ? crypto : undefined;

  const bytes = new Uint8Array(byteLength);
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let i = 0; i < byteLength; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  let out = '';
  for (let i = 0; i < bytes.length; i += 1) {
    out += HEX[(bytes[i] >> 4) & 0x0f] + HEX[bytes[i] & 0x0f];
  }
  return out;
}

export interface Traceparent {
  traceId: string;
  spanId: string;
  header: string;
}

export function newTraceparent(): Traceparent {
  const traceId = randomHex(16);
  const spanId = randomHex(8);
  return {
    traceId,
    spanId,
    header: `00-${traceId}-${spanId}-01`,
  };
}

function asUrlString(input: RequestInfo | URL): string {
  if (typeof input === 'string') {
    return input;
  }
  if (input instanceof URL) {
    return input.toString();
  }
  return input.url;
}

export function shouldPropagateTo(
  url: string,
  propagationTargets: Array<RegExp | string>,
): boolean {
  for (const target of propagationTargets) {
    if (typeof target === 'string') {
      if (url.includes(target)) {
        return true;
      }
    } else if (target.test(url)) {
      return true;
    }
  }
  return false;
}

export { asUrlString };
